import React, { useState, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';

const TIPO_WEB = ['Web estática', 'Web mediana', 'Tienda online', 'No lo sé todavía'];

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '13px 16px',
  color: '#e8edf5',
  fontSize: '0.95rem',
  outline: 'none',
  transition: 'border-color 0.2s',
  fontFamily: 'inherit',
};

const INFO = [
  { icon: 'fa-solid fa-location-dot', titulo: 'Ubicación',         texto: 'Castellón de la Plana\nComunitat Valenciana, España' },
  { icon: 'fa-solid fa-envelope',     titulo: 'Email',             texto: 'info@webfacilcastellon.es' },
  { icon: 'fa-solid fa-phone',        titulo: 'Teléfono / WhatsApp', texto: '621 06 10 16' },
];

const Contacto = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '', tipoWeb: '', presupuesto: '', mensaje: '',
  });
  const [enviado, setEnviado] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setEnviado(true);
  };

  const fieldFocusStyle = (name) => ({
    ...inputStyle,
    borderColor: focused === name ? 'rgba(46,139,192,0.7)' : inputStyle.border,
    boxShadow: focused === name ? '0 0 0 3px rgba(46,139,192,0.12)' : 'none',
  });

  return (
    <section id="contacto">
      <Container>
        <div className="text-center mb-5" ref={ref}>
          <motion.span className="badge-primary mb-3 d-inline-block" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}>
            Contacto
          </motion.span>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
            Hablemos de tu proyecto
          </motion.h2>
          <div className="accent-line center" />
          <motion.p className="section-subtitle" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
            Rellena el formulario y te respondemos en menos de 24 horas. La consulta inicial es gratuita.
          </motion.p>
        </div>

        <Row className="g-5 justify-content-center align-items-start">
          {/* Info lateral */}
          <Col lg={4}>
            <motion.div initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.3 }}>
              {INFO.map(({ icon, titulo, texto }) => (
                <div
                  key={titulo}
                  style={{
                    display: 'flex', gap: '16px', marginBottom: '1.5rem',
                    padding: '1.2rem',
                    background: 'var(--bg-card)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div style={{
                    width: '44px', height: '44px', flexShrink: 0,
                    background: 'rgba(46,139,192,0.12)',
                    borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={icon} style={{ color: '#45a8e0', fontSize: '1.1rem' }} />
                  </div>
                  <div>
                    <div style={{ color: '#45a8e0', fontSize: '0.8rem', fontWeight: 700, marginBottom: '4px' }}>{titulo}</div>
                    <div style={{ color: '#c8d8ec', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>{texto}</div>
                  </div>
                </div>
              ))}

            </motion.div>
          </Col>

          {/* Formulario */}
          <Col lg={7}>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.4 }}>
              {enviado ? (
                <div style={{
                  textAlign: 'center', padding: '4rem 2rem',
                  background: 'var(--bg-card)',
                  border: '1px solid rgba(92,184,92,0.3)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <div style={{ marginBottom: '1.2rem' }}>
                    <i className="fa-solid fa-circle-check" style={{ color: '#5cb85c', fontSize: '3.5rem' }} />
                  </div>
                  <h3 style={{ color: 'white', fontWeight: 700, marginBottom: '0.8rem' }}>¡Mensaje enviado!</h3>
                  <p style={{ color: '#8fa3bf', lineHeight: 1.7 }}>
                    Gracias por contactarnos. Te responderemos en menos de 24 horas en el email que nos has dejado.
                  </p>
                  <button
                    className="btn-outline-custom mt-3"
                    onClick={() => { setEnviado(false); setForm({ nombre: '', email: '', telefono: '', tipoWeb: '', presupuesto: '', mensaje: '' }); }}
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '2.5rem',
                  }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Nombre *</label>
                      <input type="text" name="nombre" required value={form.nombre} onChange={handleChange}
                        placeholder="Tu nombre" style={fieldFocusStyle('nombre')}
                        onFocus={() => setFocused('nombre')} onBlur={() => setFocused(null)} />
                    </Col>
                    <Col md={6}>
                      <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Email *</label>
                      <input type="email" name="email" required value={form.email} onChange={handleChange}
                        placeholder="tu@email.com" style={fieldFocusStyle('email')}
                        onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} />
                    </Col>
                    <Col md={6}>
                      <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Teléfono</label>
                      <input type="tel" name="telefono" value={form.telefono} onChange={handleChange}
                        placeholder="621 000 000" style={fieldFocusStyle('telefono')}
                        onFocus={() => setFocused('telefono')} onBlur={() => setFocused(null)} />
                    </Col>
                    <Col md={6}>
                      <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Tipo de web</label>
                      <select name="tipoWeb" value={form.tipoWeb} onChange={handleChange}
                        style={{ ...fieldFocusStyle('tipoWeb'), appearance: 'auto' }}
                        onFocus={() => setFocused('tipoWeb')} onBlur={() => setFocused(null)}>
                        <option value="">Selecciona...</option>
                        {TIPO_WEB.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Col>
                    <Col md={12}>
                      <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Cuéntanos tu proyecto *</label>
                      <textarea name="mensaje" required rows={5} value={form.mensaje} onChange={handleChange}
                        placeholder="Describe tu negocio, qué quieres conseguir con la web y cualquier detalle relevante..."
                        style={{ ...fieldFocusStyle('mensaje'), resize: 'vertical', minHeight: '120px' }}
                        onFocus={() => setFocused('mensaje')} onBlur={() => setFocused(null)} />
                    </Col>
                  </Row>

                  <div className="d-flex gap-3 align-items-center mt-4 flex-wrap">
                    <button type="submit" className="btn-accent-custom" style={{ padding: '14px 36px' }}>
                      Enviar mensaje
                      <i className="fa-solid fa-paper-plane ms-2" style={{ fontSize: '0.85rem' }} />
                    </button>
                    <span style={{ color: '#445568', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="fa-solid fa-lock" style={{ fontSize: '0.75rem' }} />
                      Tus datos son confidenciales y no serán cedidos a terceros.
                    </span>
                  </div>
                </form>
              )}
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contacto;
